import { commonTexts, settlementTexts } from 'consts/text';
import { ChildId, ChildSettlementId } from 'types';

import { AppButton, AppFlex, AppTooltipQuestionMarkIcon } from 'components/atoms';
import { Checkbox } from 'antd';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { LoadingDisplay, ErrorDisplay, EmptyDisplay } from 'components/molecules';
import { colors } from 'theme/styledComponents/colors';
import { ChildSettlementComponents } from '../../ChildSettlementComponents/ChildSettlementComponents';

type Props = {
    childId: ChildId;
    period: string;
};

export const ChildSettlementMonthlyList = ({ childId, period }: Props) => {
    const { displaySuccess, displayError } = useSimpleNotification();

    const {
        data: childSettlement,
        isLoading,
        isFetching,
        isError,
    } = useAppQuery(
        'CHILD_SETTLEMENT',
        [period, childId],
        () =>
            StaffHTTPService.settlements.getChildSettlement({
                childId,
                period,
            }),
        {},
    );

    const monthlyDetails = childSettlement?.monthlySettlementDetailsDto;
    const settlementId = monthlyDetails?.id as ChildSettlementId | undefined;
    const isSettlementVerified = !!monthlyDetails?.verified;

    const { mutate: toggleChildSettlementPaid, isLoading: isToggleChildSettlementPaidLoading } =
        useAppMutation(StaffHTTPService.settlements.toggleChildSettlementPaid, {
            key: ['TOGGLE_CHILD_SETTLEMENT_PAID'],
            onSuccess: () =>
                displaySuccess(settlementTexts.children.details.changePaidStatusSuccess),
            onError: () => displayError(commonTexts.errorMessages.actionExecution),
            invalidateQueryKeys: [
                ['SETTLEMENTS'],
                ['CHILD_SETTLEMENT'],
                ['CHILD_SETTLEMENT_BALANCE'],
                ['CHILD_SETTLEMENT_PAYMENT'],
            ],
        });

    const handlePaidStatusChange = (event: { target: { checked: boolean } }) => {
        if (!settlementId || isSettlementVerified) {
            return;
        }

        toggleChildSettlementPaid({
            settlementId,
            paid: event.target.checked,
        });
    };

    if (isLoading || isFetching) {
        return <LoadingDisplay />;
    }
    if (isError) {
        return <ErrorDisplay />;
    }
    if (!monthlyDetails) {
        return <EmptyDisplay />;
    }

    return (
        <ChildSettlementComponents
            additionalLeftTableHeaderContent={
                <AppFlex align="center" style={{ marginLeft: 16 }}>
                    <Checkbox
                        checked={monthlyDetails.paid}
                        disabled={isSettlementVerified || isToggleChildSettlementPaidLoading}
                        onChange={handlePaidStatusChange}
                    >
                        <AppFlex align="center" gap="small">
                            <span>{settlementTexts.children.details.paid}</span>
                            <AppButton
                                type="text"
                                size="small"
                                margin={0}
                                accessibilityLabel={settlementTexts.children.details.paidTooltip}
                                icon={
                                    <AppTooltipQuestionMarkIcon
                                        tooltip={settlementTexts.children.details.paidTooltip}
                                        color={colors.tooltip.tooltip3}
                                        size={16}
                                    />
                                }
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                            />
                        </AppFlex>
                    </Checkbox>
                </AppFlex>
            }
            costComponentList={monthlyDetails.costComponentList}
            summary={monthlyDetails.grossSummary}
            title={settlementTexts.children.details.monthlySettlement}
        />
    );
};
